import React, { useEffect, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import moment from "moment";
import ReactMarkdown from "react-markdown";
import CustomTable from "../../../common/CustomTable/CustomTable";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { getTasks, getTasksPolicyNumber } from "../../../store/user/tasks/tasksActions";
import { dateFormat } from "../../../constants/Constants";

interface ServiceRequestsProps {
  policyNumber?: string;
  lobCode: string;
  carrier: string;
  system: string;
  type?: string;
  onAction?: (action: any) => void;
}

const ServiceRequestsPanel: React.FC<ServiceRequestsProps> = ({
  policyNumber,
  lobCode,
  carrier,
  system,
  type,
}) => {
  
  const dispatch = useAppDispatch();

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);  
  const { tasks } = useAppSelector((state: RootState) => state.tasks);

  useEffect(() => {
    if (
      type === "service_request" &&
      userDetails &&
      userDetails.customerId &&
      userDetails.carrier &&
      userDetails.system
    ) {
      dispatch(
        getTasks({
          customerId: userDetails.customerId,
          carrier: userDetails.carrier,
          system: userDetails.system,
        })
      );
    }
  }, [
    dispatch,
    type,
    userDetails?.customerId,
    userDetails?.carrier,
    userDetails?.system,
  ]);

  useEffect(() => {
    if (
      type === "service_request_details" &&
      policyNumber &&
      userDetails &&
      userDetails.customerId &&
      userDetails.carrier &&
      userDetails.system
    ) {
      dispatch(
        getTasksPolicyNumber({
          policyNumber,
          customerId: userDetails.customerId,
          carrier: userDetails.carrier,
          system: userDetails.system,
        })
      );
    }
  }, [
    dispatch,
    policyNumber,
    type,
    userDetails?.customerId,
    userDetails?.carrier,
    userDetails?.system,
  ]);
  const columns = useMemo(
    () => [
      {
        label: "Transaction Date",
        accessorKey: "addDt",
        Cell: (task: any) => (
          <Typography sx={{ fontSize: 12, color: "#111827" }}>
            {task.addDt ? moment(task.addDt).format(dateFormat) : "-"}
          </Typography>
        ),
      },
      {
        label: "Category",
        accessorKey: "name",
      },
      {
        label: "Request Name",
        accessorKey: "description",
      },
      {
        label: "Description",
        accessorKey: "note",
        Cell: (task: any) => (
          <Box sx={{ maxWidth: 380, "& .markdown": { fontSize: 12 } }}>
            <ReactMarkdown className="markdown">{task.note}</ReactMarkdown>
          </Box>
        ),
      },
      {
        label: "Status",
        accessorKey: "statusCd",
        Cell: (task: any) => (
          <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
            {task.statusCd}
          </Typography>
        ),
      },
    ],
    []
  );

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort(
        (a, b) => new Date(b.addDt).getTime() - new Date(a.addDt).getTime()
      ),
    [tasks]
  );

  const showNoTasksForPolicy =
    type === "service_request_details" && sortedTasks.length === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        border: "1px solid #e5e7eb",
        borderRadius: { xs: 1, md: 2 },
        p: { xs: 1, md: 1.5 },
        bgcolor: "#ffffff",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>
              Service Requests - {sortedTasks.length} request
            </Typography>
            <Typography
              sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280", mt: 0.25 }}
            >
              Manage and track your policy service requests
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1 }}>
          {showNoTasksForPolicy ? (
            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280", py: 3, textAlign: "center", fontWeight: "bold", }}>
              No Service Request found for the given Policy Number
            </Typography>
          ) : (
            <CustomTable
              columns={columns}
              data={sortedTasks}
              stickyHeader={true}
              pagination={false}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ServiceRequestsPanel;


